import {
  Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';
import {
  take,
  takeUntil
} from 'rxjs';

import {
  IconSizes, IconUsageTypes, IrisIconModule
} from '@sondermind/iris/icon';
import { IrisModalService } from '@sondermind/iris/modal';

import { BreakpointsService } from '@sondermind/utilities/services';
import { IInsuranceOptionValue } from '@sondermind/utilities/models-provider-network-company';

import { MoreFiltersModalComponent } from '../more-filters-modal/more-filters-modal.component';
import { IMoreFiltersConfig, IMoreFiltersSelections } from '../../models/more-filters.interface';
import { IFilterForm } from '../../models/filter-form.interface';
import { IOptionCategory } from '../../models/option-category.interface';

@Component({
  selector: 'more-filters',
  standalone: true,
  imports: [
    CommonModule,
    IrisIconModule
  ],
  templateUrl: './more-filters.component.html',
  styleUrls: ['./more-filters.component.scss']
})
export class MoreFiltersComponent implements OnInit {
  @Input() insuranceOptions: Array<IOptionCategory<IInsuranceOptionValue>> = [];
  @Input() genderOptions: Array<IOptionCategory<number>> = [];
  @Input() locationOptions: Array<IOptionCategory<string>> = [];
  @Input() filterFormGroup!: FormGroup<IFilterForm>;
  @Output() moreFiltersSelections = new EventEmitter<IMoreFiltersSelections>();

  decorativeIconUsage = IconUsageTypes.DECORATIVE;
  mediumIconSize = IconSizes.MD;

  numSelections: number = 0;

  constructor(
    private breakpointService: BreakpointsService,
    private irisModalService: IrisModalService
  ) {}

  ngOnInit(): void {
    const formValues = this.filterFormGroup.value;
    this.numSelections = this.getNumberOfFiltersSelected(
      formValues.state ?? null,
      formValues.insurance ?? null,
      formValues.genders ?? []
    );
  }

  openFiltersModal(): void {
    const modalConfig = this.buildModalConfig();

    /* eslint-disable @typescript-eslint/indent */
    const modalRef = this.irisModalService
      .openUnstyledFullScreenOverlay<MoreFiltersModalComponent, IMoreFiltersConfig, IMoreFiltersSelections>(
        MoreFiltersModalComponent,
        modalConfig
      );
    /* eslint-enable @typescript-eslint/indent */

    this.breakpointService.toggleModal(true);

    modalRef.afterClosed$().pipe(
      take(1)
    ).subscribe((selections) => {
      if (selections) {
        this.numSelections = this.getNumberOfFiltersSelected(
          selections.locationSelection,
          selections.insuranceSelection,
          selections.genderSelections
        );

        this.moreFiltersSelections.emit(selections);
      }
      this.breakpointService.toggleModal(false);
    });

    this.breakpointService.isMobile$.pipe(
      takeUntil(modalRef.afterClosed$())
    ).subscribe((isMobile) => {
      if (!isMobile) {
        modalRef.close();
      }
    });
  }

  private buildModalConfig(): IMoreFiltersConfig {
    const formValues = this.filterFormGroup.value;
    this.numSelections = this.getNumberOfFiltersSelected(
      formValues.state ?? null,
      formValues.insurance ?? null,
      formValues.genders ?? []
    );

    return {
      insuranceSelect: {
        currentSelection: formValues.insurance ?? null,
        categorizedOptions: this.insuranceOptions
      },
      genderSelect: {
        currentSelections: formValues.genders ?? [],
        options: this.genderOptions[0]?.options ?? []
      },
      locationSelect: {
        currentSelection: formValues.state ?? null,
        options: this.locationOptions[0]?.options ?? []
      }
    };
  }

  private getNumberOfFiltersSelected(
    locationSelection: string | null, insuranceSelection: IInsuranceOptionValue | null, genders: number[]
  ): number {
    const locationSelected: number = locationSelection ? 1 : 0;
    const insuranceSelected: number = insuranceSelection ? 1 : 0;
    const gendersSelected: number = genders ? genders.length : 0;
    return gendersSelected + locationSelected + insuranceSelected;
  }
}
